import React, { useEffect, useState, useRef, useCallback } from 'react';
import { canCatalogue, getAccess, can as canCap } from '../permissions/access';
import '../multiLocation/multiLocation.css';
import LocationScope, { LOCATION_SCOPE_ALL, LOCATION_SCOPE_THIS, resolveScopeSalonIds } from '../multiLocation/LocationScope';
import useSites, { groupSites } from '../multiLocation/useSites';
import { syncCatalogueAfterSave } from '../multiLocation/syncCatalogue';
import { useDispatch, useSelector } from 'react-redux';
import SlidePanel from '../shared/SlidePanel';
import { newServicesAction } from '../../_actions';
import { formService } from '../../_services/form.service';
import InlinePickerSelect from '../shared/InlinePickerSelect';
import toast from '../shared/Toast';
import ColourPicker from '../shared/ColourPicker';
import '../shared/ui.css';
import './ServiceEditorPanel.css';
import TaxPicker from '../taxes/TaxPicker';
import axios from 'axios';
import API from '../../config/API';
import { markFirstTask } from '../onboarding/firstTaskRegistry';

const Toggle = ({ checked, onChange, onClick }) => (
    <button
        type="button"
        className={`sep-toggle ${checked ? 'sep-toggle--on' : ''}`}
        onClick={(e) => {
            if (onClick) onClick(e);
            onChange(!checked);
        }}
    >
        <span className="sep-toggle-knob" />
    </button>
);

const STEPS = [
    { id: 'detail', number: 1, label: 'Service Details' },
    { id: 'team', number: 2, label: 'Team Setup' },
];

// Default client-facing copy for the finishing-service and consultation rules.
// Pre-filled when the rule is toggled on (or loaded enabled-but-empty) so
// salons have ready-to-go wording they can tweak rather than placeholder text
// that vanishes when they start typing.
const DEFAULT_FINISHING_MESSAGE = 'This service requires a finishing service to complete your look. Please add one to your booking.';
const DEFAULT_CONSULTATION_MESSAGE = 'A consultation is required before your first visit so we can understand your needs and ensure the best results.';

const ServiceEditorPanel = ({ serviceId, onClose, onSaved, inline, onBack }) => {
    const dispatch = useDispatch();
    const slidePanelRef = useRef(null);
    const imageInputRef = useRef(null);

    const listCategory = useSelector(state => state.newServices.listCategory);
    const listHairdresser = useSelector(state => state?.newServices?.listHairdresser);
    const userInfo = useSelector(state => state.user);
    // Roles & permissions: or an employed team member whose role has `catalogue`.
    const isSalonManager = userInfo?.userInfo?.hairdresser?.isSalonOwner === 1
        || (userInfo?.userInfo?.hairdresser?.employmentTypeId !== 2 && canCatalogue(false));
    // Commission is money: the per-service override is the owner's (the
    // account holder here, or the group owner working at this location) or a
    // site manager's who also runs the catalogue. Never a plain `catalogue`
    // role; the server applies the same rule.
    const siteInfo = useSites();
    const isOwner = userInfo?.userInfo?.hairdresser?.isSalonOwner === 1
        || !!(getAccess() && getAccess().owner)
        || !!(siteInfo.active && siteInfo.active.role === 'manager' && canCap('catalogue'));
    // The active site's group (none for a salon on its own, or for a
    // self-employed stylist whose other salon is a different business).
    const groupSiteList = groupSites({ sites: siteInfo.sites, group: siteInfo.group });
    const inGroup = groupSiteList.length > 1;
    // Self-employed staff get a per-service salon-commission override; the
    // staff list carries their type and default rate.
    const seStaffInfo = (hairdresserId) => {
        const h = (listHairdresser || []).find(hd => hd._id === hairdresserId);
        return h && Number(h.employmentTypeId) === 2 && Number(h.isSalonOwner) !== 1 ? h : null;
    };
    const currencySymbol = userInfo?.userInfo?.hairdresser?.currencySymbol || '£';

    const [activeTab, setActiveTab] = useState('detail');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    const [serviceData, setServiceData] = useState({
        serviceName: '',
        description: '',
        category: null,
        defaultDiaryColour: '#c8eeee',
        serviceImages: [],
        isFeaturedForSalon: false,
        staffSettings: [],
    });

    const [availableAutoForms, setAvailableAutoForms] = useState([]);
    const [autoAttachedForms, setAutoAttachedForms] = useState([]);
    const [selectedStaffIndex, setSelectedStaffIndex] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [allServices, setAllServices] = useState([]);
    const [finishingSearch, setFinishingSearch] = useState('');
    const [consultationSearch, setConsultationSearch] = useState('');
    // Per-service map of which stylists offer each service online:
    // { <serviceId>: [hairdresserId, ...] }. Drives greying of the Booking
    // Rules pickers — a service no relevant stylist offers online can't be
    // added in the booking flow, so picking it would create an
    // unsatisfiable (fail-closed) gate.
    const [onlineStaffMap, setOnlineStaffMap] = useState({});

    const categoryOptions = listCategory?.map(cat => ({
        value: cat?._id,
        label: cat?.categoryName,
    }));

    // Is `svcId` online-bookable by the stylist(s) relevant to this gate?
    //   sameStylist === true  → must be offered online by THIS stylist
    //   sameStylist === false → offered online by ANY stylist is enough
    // A service no relevant stylist offers online can't be added in the
    // booking flow, so it can't satisfy the gate — grey it out here so the
    // salon can't configure an unsatisfiable rule.
    const isServiceBookableOnline = (svcId, sameStylist, hairdresserId) => {
        const ids = onlineStaffMap[String(svcId)] || [];
        return sameStylist ? ids.includes(String(hairdresserId)) : ids.length > 0;
    };

    // --- Data loading ---
    useEffect(() => {
        // Categories + staff list + auto-form list are needed for both
        // creating and editing, so load them regardless of serviceId.
        dispatch(newServicesAction.listCategoryData({}));
        dispatch(newServicesAction.listHairDresserData({}));

        formService.listForms({ status: 'active', formType: 'automatic' })
            .then(response => {
                const forms = response.data?.responseData?.result || [];
                setAvailableAutoForms(forms);
                const autoForms = forms
                    .filter(f => {
                        const s = f.settings || {};
                        if (s.formRequiredFor === 'every_service') return true;
                        if (s.formRequiredFor === 'specific_services') return (s.specificServices || []).includes(serviceId);
                        return false;
                    })
                    .map(f => ({ formId: f._id || f.id, formName: f.name, isRequired: true, isAutoAttached: true }));
                setAutoAttachedForms(autoForms);
            })
            .catch(() => {});

        // Load all services for booking-rules pickers (used in Team Setup)
        dispatch(newServicesAction.listServicesData({}))
            .then(res => {
                const services = [];
                (res?.listServices || []).forEach(cat => {
                    (cat.serviceList || []).forEach(svc => {
                        services.push({ id: svc.commonServiceId || svc._id, name: svc.serviceName });
                    });
                });
                setAllServices(services.filter(s => s.id !== serviceId));
            })
            .catch(() => {});

        // Which stylists offer each service online — for greying out
        // non-bookable targets in the finishing / consultation pickers.
        dispatch(newServicesAction.getOnlineStaffMap())
            .then(res => setOnlineStaffMap(res?.onlineStaffMap || {}))
            .catch(() => {});

        // No serviceId → creating a new service. Skip the fetch and start
        // the editor immediately with empty defaults instead of stalling
        // the panel on "Loading…".
        if (!serviceId) {
            setLoading(false);
            return;
        }

        dispatch(newServicesAction.viewServiceData({ commonServiceId: serviceId }))
            .then(res => {
                const data = res?.viewService;
                if (data) loadServiceData(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [serviceId, dispatch]);

    const loadServiceData = useCallback((data) => {
        const defaultColor = data?.diaryColour || '#c8eeee';
        const isFeatured = data?.hairdresserService?.some(item => item.isFeaturedForSalon) || false;

        const existingReq = data?.hairdresserService?.find(item =>
            Array.isArray(item.resourceRequirements) && item.resourceRequirements.length);
        setResourceGroupId(existingReq ? String(existingReq.resourceRequirements[0].groupId) : null);
        setResourceUnitId(existingReq && existingReq.resourceRequirements[0].resourceId
            ? String(existingReq.resourceRequirements[0].resourceId) : null);

        setServiceData({
            serviceName: data?.serviceName || '',
            description: data?.serviceDescription || '',
            category: null, // set after categoryOptions loads
            defaultDiaryColour: defaultColor,
            serviceImages: data?.serviceImages || [],
            isFeaturedForSalon: isFeatured,
            staffSettings: (data?.hairdresserService || []).map(item => {
                const staffColor = item.diaryColour || defaultColor;
                return {
                    hairdresserId: item.hairdresserId,
                    name: item.name || '',
                    servicePrice: parseFloat(item.servicePrice) || 0,
                    serviceTime: parseFloat(item.serviceTime) || 0,
                    isDeactivated: !item.isDeactivated || false,
                    depositPrice: parseFloat(item.depositPrice) || 0,
                    onlineBooking: item.onlineBooking || false,
                    processingTime: parseFloat(item.processingTime) || 0,
                    finishingTime: parseFloat(item.finishingTime) || 0,
                    // Per-service salon commission (self-employed only). ''
                    // = inherit the stylist's staff rate. _origCommission lets
                    // Save send it only when the owner actually changed it.
                    salonCommissionRate: item.salonCommissionRate ?? '',
                    _origCommission: item.salonCommissionRate ?? '',
                    setupTime: parseFloat(item.setupTime) || 0,
                    cleanupTime: parseFloat(item.cleanupTime) || 0,
                    requiresFinishing: item.requiresFinishing || false,
                    finishingServiceIds: item.finishingServiceIds || [],
                    finishingSameStylist: item.finishingSameStylist !== false,
                    requiresConsultation: item.requiresConsultation || false,
                    consultationServiceId: item.consultationServiceId || null,
                    consultationSameStylist: item.consultationSameStylist !== false,
                    finishingMessage: item.finishingMessage || (item.requiresFinishing ? DEFAULT_FINISHING_MESSAGE : ''),
                    consultationMessage: item.consultationMessage || (item.requiresConsultation ? DEFAULT_CONSULTATION_MESSAGE : ''),
                    diaryColour: staffColor,
                    isColourOverridden: item.diaryColour && item.diaryColour !== defaultColor,
                    staffServiceImages: item.staffServiceImages || [],
                    isFeaturedForStaff: item.isFeaturedForStaff || false,
                    linkedForms: (item.linkedForms || []).map(lf => ({
                        formId: lf.formId, formName: lf.formName, isRequired: lf.isRequired !== false,
                    })),
                };
            }),
            _rawCategoryId: data?.categoryID,
        });
    }, []);

    // Set category once categoryOptions are available
    useEffect(() => {
        if (serviceData._rawCategoryId && categoryOptions?.length) {
            const match = categoryOptions.find(c => c.value === serviceData._rawCategoryId);
            if (match && !serviceData.category) {
                setServiceData(prev => ({ ...prev, category: match }));
            }
        }
    }, [categoryOptions, serviceData._rawCategoryId]);

    // Once the hairdresser list lands, either:
    //   - For an EXISTING service, refresh the staff names against the
    //     latest hairdresser data; or
    //   - For a NEW service, seed staffSettings with one entry per
    //     hairdresser (all enabled, default values) so the Team Setup tab
    //     shows the salon's staff right away.
    useEffect(() => {
        if (!listHairdresser?.length) return;
        if (serviceData.staffSettings.length) {
            setServiceData(prev => ({
                ...prev,
                staffSettings: prev.staffSettings.map(staff => {
                    const h = listHairdresser.find(hd => hd._id === staff.hairdresserId);
                    return h ? { ...staff, name: h.name || staff.name } : staff;
                }),
            }));
            return;
        }
        if (serviceId) return; // existing service still loading data
        const defaultColor = serviceData.defaultDiaryColour || '#c8eeee';
        setServiceData(prev => ({
            ...prev,
            staffSettings: listHairdresser.map(h => ({
                hairdresserId: h._id,
                name: h.name || '',
                servicePrice: 0,
                serviceTime: 0,
                isDeactivated: false, // disabled by default — staff opt in per service
                depositPrice: 0,
                onlineBooking: false,
                processingTime: 0,
                finishingTime: 0,
                salonCommissionRate: '',
                _origCommission: '',
                requiresFinishing: false,
                finishingServiceIds: [],
                finishingSameStylist: true,
                requiresConsultation: false,
                consultationServiceId: null,
                consultationSameStylist: true,
                finishingMessage: '',
                consultationMessage: '',
                diaryColour: defaultColor,
                isColourOverridden: false,
                staffServiceImages: [],
                isFeaturedForStaff: false,
                linkedForms: [],
            })),
        }));
    }, [listHairdresser, serviceId]);

    // Rooms/equipment: which group (if any) this service needs. Service-wide
    // — the requirement stamps onto every staff row at save (docs/resources.md
    // §3, the variants-reveal pattern). No groups = control hidden entirely:
    // a salon without resources never sees it (R1 for the eyes).
    const [resourceGroups, setResourceGroups] = useState([]);
    const [resourceUnits, setResourceUnits] = useState([]);
    const [resourceGroupId, setResourceGroupId] = useState(null);
    // Optional: THIS unit only ("the laser is in Room 2"). null = any in group.
    const [resourceUnitId, setResourceUnitId] = useState(null);
    useEffect(() => {
        axios.get(API.RESOURCE_LIST, { headers: { accessToken: localStorage.getItem('accessToken') } })
            .then(res => {
                setResourceGroups(res?.data?.responseData?.groups || []);
                setResourceUnits((res?.data?.responseData?.resources || []).filter(u => u.isActive !== false));
            })
            .catch(() => {});
    }, []);
    // Picker values encode group[:unit] so one InlinePickerSelect covers both.
    const singular = (name) => (name || 'resource').replace(/s$/, '').toLowerCase();
    const resourceOptions = [{ value: '', label: 'None' }].concat(
        resourceGroups.flatMap(g => [
            { value: String(g._id), label: `Any ${singular(g.name)}` },
            ...resourceUnits.filter(u => String(u.groupId) === String(g._id))
                .map(u => ({ value: `${g._id}:${u._id}`, label: `${u.name} only` })),
        ]));
    const resourceValue = resourceGroupId
        ? (resourceOptions.find(o => o.value === (resourceUnitId ? `${resourceGroupId}:${resourceUnitId}` : resourceGroupId))
            || { value: resourceGroupId, label: `Any ${singular(resourceGroups.find(g => String(g._id) === resourceGroupId)?.name)}` })
        : { value: '', label: 'None' };

    // Auto-grow refs for the Service Name + Description textareas so long
    // text isn't clipped on mobile. RAF defers measurement until after
    // layout — scrollHeight isn't accurate when the SlidePanel is still
    // animating in or React has just landed loaded data.
    const serviceNameRef = useRef(null);
    const descriptionRef = useRef(null);
    const autoGrow = (el) => {
        if (!el) return;
        requestAnimationFrame(() => {
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
        });
    };
    const setServiceNameRef = useCallback((el) => {
        serviceNameRef.current = el;
        autoGrow(el);
    }, []);
    const setDescriptionRef = useCallback((el) => {
        descriptionRef.current = el;
        autoGrow(el);
    }, []);
    useEffect(() => { autoGrow(serviceNameRef.current); }, [serviceData.serviceName]);
    useEffect(() => { autoGrow(descriptionRef.current); }, [serviceData.description]);

    // --- Field handlers ---
    const updateField = (field, value) => {
        setServiceData(prev => ({ ...prev, [field]: value }));
        setDirty(true);
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    // Default duration applied when a staff member is first enabled without
    // one set. Enabling a stylist with duration 0 is invalid (validate()
    // rejects it), so give them a sensible starting value rather than letting
    // Save silently fail on a field the list view never surfaces.
    const DEFAULT_SERVICE_TIME = 30;

    const updateStaffField = (index, field, value) => {
        setServiceData(prev => ({
            ...prev,
            staffSettings: prev.staffSettings.map((s, i) => {
                if (i !== index) return s;
                const updated = { ...s, [field]: value };
                // When turning a staff member on, seed a default duration if
                // they don't already have one. Avoids an invisible validation
                // failure on Save from the Team Setup list.
                if (field === 'isDeactivated' && value && (!s.serviceTime || parseFloat(s.serviceTime) === 0)) {
                    updated.serviceTime = DEFAULT_SERVICE_TIME;
                }
                return updated;
            }),
        }));
        setDirty(true);
        // Clear the validation error tied to this field as soon as it
        // changes — the user is responding to the error, don't leave it
        // displayed once they've addressed it.
        setErrors(prev => {
            const next = { ...prev };
            if (next[`${field}_${index}`]) next[`${field}_${index}`] = '';
            // Enabling a staff member seeds a default duration, so a prior
            // "Duration can't be 0" error for them no longer applies.
            if (field === 'isDeactivated' && value && next[`serviceTime_${index}`]) next[`serviceTime_${index}`] = '';
            // Toggling the rule OFF makes the linked-service error moot.
            if (field === 'requiresConsultation' && !value) next[`consultationServiceId_${index}`] = '';
            if (field === 'requiresFinishing' && !value) next[`finishingServiceIds_${index}`] = '';
            return next;
        });
    };

    // --- Image handling ---
    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('image', file);

        dispatch(newServicesAction.uploadServiceImage(formData))
            .then(res => {
                const url = res?.uploadImage?.responseData?.url || res?.url;
                if (url) {
                    updateField('serviceImages', [...serviceData.serviceImages, { url }]);
                }
            })
            .catch(err => console.error('Image upload error:', err));
        e.target.value = '';
    };

    const handleImageDelete = (index) => {
        updateField('serviceImages', serviceData.serviceImages.filter((_, i) => i !== index));
    };

    // --- Validation ---
    const validate = () => {
        const newErrors = {};
        if (!serviceData.serviceName?.trim()) newErrors.serviceName = 'Service name is required';
        if (!serviceData.category) newErrors.category = 'Category is required';

        serviceData.staffSettings.forEach((staff, i) => {
            if (staff.isDeactivated && (!staff.serviceTime || staff.serviceTime === 0)) {
                newErrors[`serviceTime_${i}`] = "Duration can't be 0 when enabled";
            } else if (staff.serviceTime && staff.serviceTime % 5 !== 0) {
                newErrors[`serviceTime_${i}`] = 'Must be a multiple of 5';
            }
            if (staff.processingTime && staff.processingTime % 5 !== 0) {
                newErrors[`processingTime_${i}`] = 'Must be a multiple of 5';
            }
            if (staff.finishingTime && staff.finishingTime % 5 !== 0) {
                newErrors[`finishingTime_${i}`] = 'Must be a multiple of 5';
            }
            if (parseFloat(staff.depositPrice) > parseFloat(staff.servicePrice)) {
                newErrors[`depositPrice_${i}`] = 'Deposit cannot exceed price';
            }
            if (staff.salonCommissionRate !== '' && staff.salonCommissionRate != null) {
                const rate = parseFloat(staff.salonCommissionRate);
                if (Number.isNaN(rate) || rate < 0 || rate > 100) {
                    newErrors[`salonCommissionRate_${i}`] = 'Must be between 0 and 100';
                }
            }
            // Enabling "First visit consultation" without picking which
            // service to gate to is a dead-end on the client side — the
            // modal can never name a service or swap the basket.
            if (staff.requiresConsultation && !staff.consultationServiceId) {
                newErrors[`consultationServiceId_${i}`] = 'Pick a consultation service below';
            }
            // Same shape for finishing — at least one allowed finishing
            // service must be selected when the rule is on.
            if (staff.requiresFinishing && (!Array.isArray(staff.finishingServiceIds) || staff.finishingServiceIds.length === 0)) {
                newErrors[`finishingServiceIds_${i}`] = 'Pick at least one finishing service below';
            }
        });

        setErrors(newErrors);
        return newErrors;
    };

    // Make a failed Save visible. The per-staff errors only render inside a
    // staff's detail sub-screen, so a validation failure from the Team Setup
    // list (or Service Details tab) would otherwise look like a dead button.
    // Fire a toast and jump the user to the first offending field.
    const surfaceValidationErrors = (validationErrors) => {
        const keys = Object.keys(validationErrors).filter(k => validationErrors[k]);
        if (keys.length === 0) return;

        // Service Details fields take priority — they live on the first tab.
        if (validationErrors.serviceName || validationErrors.category) {
            setActiveTab('detail');
            toast.error(validationErrors.serviceName || validationErrors.category);
            return;
        }

        // Otherwise it's a per-staff error. Open that staff's detail so the
        // inline message has somewhere to show, and toast the reason.
        const staffKey = keys.find(k => /_\d+$/.test(k));
        if (staffKey) {
            const index = parseInt(staffKey.split('_').pop(), 10);
            setActiveTab('team');
            if (!Number.isNaN(index)) setSelectedStaffIndex(index);
            toast.error(validationErrors[staffKey]);
            return;
        }

        toast.error('Please fix the highlighted fields before saving');
    };

    // --- Save ---
    // Multi-location: is this service linked to other locations, and which scope does this edit have?
    const [links, setLinks] = useState(null);
    // Locations control: where this create/edit lands. A new service is a
    // menu item and starts at every location (the chips show it, and the
    // owner can untick). An EDIT never starts at every location: it starts
    // at this location only, then picks up the locations the service is
    // already linked to, so saving a price change can never quietly create
    // the service somewhere new.
    const [locScope, setLocScope] = useState(serviceId ? LOCATION_SCOPE_THIS : LOCATION_SCOPE_ALL);
    const scopeTouched = useRef(false);
    const changeLocScope = (v) => { scopeTouched.current = true; setLocScope(v); };
    useEffect(() => {
        scopeTouched.current = false;
        setLocScope(serviceId ? LOCATION_SCOPE_THIS : LOCATION_SCOPE_ALL);
        if (!serviceId || !inGroup) { setLinks(null); return; }
        let live = true;
        require('../../_services/sites.service').sitesService.serviceLinks(serviceId).then((l) => {
            if (!live) return;
            setLinks(l);
            const ids = ((l && l.sites) || []).map((x) => String(x.salonId));
            if (!scopeTouched.current && ids.length) setLocScope({ mode: 'choose', salonIds: ids });
        }).catch(() => { if (live) setLinks(null); });
        return () => { live = false; };
    }, [serviceId, inGroup]);

    const handleSave = () => {
        if (!dirty) {
            handleClose(true);
            return;
        }
        const validationErrors = validate();
        if (Object.keys(validationErrors).some(k => validationErrors[k])) {
            surfaceValidationErrors(validationErrors);
            return;
        }
        setSaving(true);
        const defaultColor = serviceData.defaultDiaryColour || '#c8eeee';

        // No serviceId means we're creating, not editing. The two paths hit
        // different endpoints with differently-shaped responses, so branch
        // here — sending an edit with an undefined commonServiceId silently
        // saves nothing.
        const isCreating = !serviceId;

        const payload = {
            ...(isCreating ? {} : { commonServiceId: serviceId }),
            // Multi-location §15d: 'all' fans name/description/category out to linked sites; 'site' keeps this edit here.
            // With the Locations control set to this location only, an edit stays here (customised).
            // The exact locations picked travel with the edit (scopeSalonIds),
            // so unticking one keeps the change away from it.
            ...(links && links.linked ? (() => {
                const active = groupSiteList.find((x) => x.isActive);
                const ids = resolveScopeSalonIds(locScope, groupSiteList, active && active.salonId);
                return locScope.mode === 'this' || !ids.length ? { scope: 'site' } : { scope: 'all', scopeSalonIds: ids };
            })() : {}),
            serviceName: serviceData.serviceName,
            categoryID: serviceData.category?.value || '',
            serviceDescription: serviceData.description,
            diaryColour: defaultColor,
            hairdresserService: serviceData.staffSettings.map(staff => {
                const staffColor = staff.isColourOverridden ? (staff.diaryColour || defaultColor) : defaultColor;
                return {
                    hairdresserId: staff.hairdresserId,
                    servicePrice: parseFloat(staff.servicePrice) || 0,
                    serviceTime: parseFloat(staff.serviceTime) || 0,
                    isDeactivated: !staff.isDeactivated,
                    depositPrice: parseFloat(staff.depositPrice) || 0,
                    onlineBooking: staff.onlineBooking,
                    processingTime: parseFloat(staff.processingTime) || 0,
                    finishingTime: parseFloat(staff.finishingTime) || 0,
                    setupTime: parseFloat(staff.setupTime) || 0,
                    cleanupTime: parseFloat(staff.cleanupTime) || 0,
                    requiresFinishing: staff.requiresFinishing || false,
                    finishingServiceIds: staff.finishingServiceIds || [],
                    finishingSameStylist: staff.finishingSameStylist !== false,
                    requiresConsultation: staff.requiresConsultation || false,
                    consultationServiceId: staff.consultationServiceId || null,
                    consultationSameStylist: staff.consultationSameStylist !== false,
                    finishingMessage: staff.finishingMessage || '',
                    consultationMessage: staff.consultationMessage || '',
                    diaryColour: staffColor,
                    isColourOverridden: staff.isColourOverridden || false,
                    linkedForms: (staff.linkedForms || []).map(f => ({
                        formId: f.formId, formName: f.formName, isRequired: f.isRequired !== false,
                    })),
                    resourceRequirements: resourceGroupId
                        ? [{ groupId: resourceGroupId, ...(resourceUnitId ? { resourceId: resourceUnitId } : {}) }]
                        : [],
                    // Owner-only and only when changed; blank clears back to
                    // the staff rate (server writes null).
                    ...(isOwner && String(staff.salonCommissionRate ?? '') !== String(staff._origCommission ?? '')
                        ? { salonCommissionRate: staff.salonCommissionRate === '' ? null : parseFloat(staff.salonCommissionRate) }
                        : {}),
                };
            }),
        };

        const action = isCreating
            ? newServicesAction.createServicesData(payload)
            : newServicesAction.editServicesData(payload);

        dispatch(action)
            .then(res => {
                // Create resolves under `newServices`, edit under `editServices`.
                const result = isCreating ? res?.newServices : res?.editServices;
                if (result?.statusCode === 1) {
                    toast.success(result?.responseData?.message || (isCreating ? 'Service created' : 'Service saved'));
                    if (!isCreating) markFirstTask('menu'); // onboarding: made the menu their own
                    // Locations control: push to the chosen sites (create there or update shared fields).
                    const createdRows = result?.responseData?.result;
                    const commonId = isCreating
                        ? (Array.isArray(createdRows) && createdRows[0] && createdRows[0].commonServiceId) || (createdRows && createdRows.commonServiceId)
                        : serviceId;
                    if (commonId) syncCatalogueAfterSave('service', String(commonId), locScope);
                    setDirty(false);
                    if (onSaved) onSaved();
                    handleClose(true);
                } else {
                    toast.error(result?.error?.responseMessage || 'Failed to save');
                }
                setSaving(false);
            })
            .catch(() => {
                toast.error('Failed to save service');
                setSaving(false);
            });
    };

    const handleClose = (force = false) => {
        if (!force && dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        if (inline && onBack) {
            onBack();
            return;
        }
        if (slidePanelRef.current?.handleClose) {
            slidePanelRef.current.handleClose();
        }
        setTimeout(() => onClose(), 280);
    };

    // --- Enabled staff count ---
    const enabledCount = serviceData.staffSettings.filter(s => s.isDeactivated).length;
    const totalCount = serviceData.staffSettings.length;

    // ──────────────── RENDER ────────────────

    const renderDetailTab = () => (
        <>
            {/* Service Details */}
            <div className="sep-section">
                <p className="sep-section-label">Service Details</p>
                <LocationScope value={locScope} onChange={changeLocScope} help="Name, description and category are shared. Price and duration stay per location." />
                {links && links.linked && links.customisedHere && links.customisedHere.length > 0 && (
                    <p className="ml-muted" style={{ margin: '-6px 0 10px' }}>Customised here: {links.customisedHere.join(', ')}</p>
                )}

                <div className="sep-field">
                    <label className="sep-field-label">Service Name</label>
                    <textarea
                        ref={setServiceNameRef}
                        className={`sep-input sep-input-grow ${errors.serviceName ? 'sep-input--error' : ''}`}
                        rows={1}
                        value={serviceData.serviceName}
                        onChange={(e) => updateField('serviceName', e.target.value.replace(/\n/g, ''))}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                        placeholder="Enter service name"
                    />
                    {errors.serviceName && <span className="sep-error">{errors.serviceName}</span>}
                </div>

                <div className="sep-field">
                    <label className="sep-field-label">Category</label>
                    <InlinePickerSelect
                        value={serviceData.category}
                        onChange={(val) => updateField('category', val)}
                        options={categoryOptions || []}
                        placeholder="Select category"
                        searchPlaceholder="Search categories..."
                        createLabel="Create new category"
                        onCreateNew={async (categoryName) => {
                            const res = await dispatch(
                                newServicesAction.createCategoryData({ categoryName })
                            );
                            if (res?.newCategory?.statusCode !== 1) {
                                toast.error(
                                    res?.newCategory?.error?.responseMessage ||
                                    'Failed to add category'
                                );
                                throw new Error('create-category-failed');
                            }
                            const created = res?.newCategory?.responseData?.result;
                            // Refresh the list so the new category appears in the
                            // options, then select it.
                            await dispatch(newServicesAction.listCategoryData({}));
                            if (created) {
                                updateField('category', {
                                    value: created._id,
                                    label: created.categoryName,
                                });
                            }
                            toast.success('Category added');
                        }}
                        error={errors.category}
                    />
                    {errors.category && <span className="sep-error">{errors.category}</span>}
                </div>

                <div className="sep-field">
                    <label className="sep-field-label">Description</label>
                    <textarea
                        ref={setDescriptionRef}
                        className="sep-input sep-textarea sep-input-grow"
                        value={serviceData.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        placeholder="Describe this service..."
                    />
                </div>
                {resourceGroups.length > 0 && (
                    <div className="sep-field">
                        <label className="sep-field-label">Needs a resource</label>
                        <InlinePickerSelect
                            options={resourceOptions}
                            value={resourceValue}
                            onChange={(sel) => {
                                const [gid, uid] = String(sel && sel.value ? sel.value : '').split(':');
                                setResourceGroupId(gid || null);
                                setResourceUnitId(uid || null);
                                // Without this, Save sees a clean form and closes without
                                // sending anything — the requirement silently never saved.
                                setDirty(true);
                            }}
                        />
                        <p className="sep-hint">
                            {resourceUnitId
                                ? 'Only this one. Online booking offers times when it is free, and Beu can\'t move these bookings to another room on busy days.'
                                : 'Online booking will only offer times when one is free, and the diary shows which one each booking gets.'}
                        </p>
                    </div>
                )}
            </div>

            {/* Images */}
            <div className="sep-section">
                <p className="sep-section-label">Images</p>
                <div className="sep-images-grid">
                    {serviceData.serviceImages.map((img, i) => (
                        <div key={i} className="sep-image-item">
                            <img src={img?.url || img} alt="" />
                            <button className="sep-image-delete" onClick={() => handleImageDelete(i)}>×</button>
                        </div>
                    ))}
                    <div className="sep-image-upload" onClick={() => imageInputRef.current?.click()}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        <span>Add</span>
                    </div>
                </div>
                <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                />
            </div>

            {/* Online Booking */}
            {isSalonManager && (
                <div className="sep-section">
                    <p className="sep-section-label">Online Booking</p>
                    <div className="sep-toggle-row">
                        <div className="sep-toggle-info">
                            <div className="sep-toggle-title">Featured for Salon</div>
                            <div className="sep-toggle-desc">Shown in featured section on booking page</div>
                        </div>
                        <Toggle
                            checked={serviceData.isFeaturedForSalon}
                            onChange={(val) => updateField('isFeaturedForSalon', val)}
                        />
                    </div>
                </div>
            )}

            {/* Diary Colour */}
            <div className="sep-section">
                <p className="sep-section-label">Diary Colour</p>
                <ColourPicker
                    value={serviceData.defaultDiaryColour}
                    onChange={(hex) => updateField('defaultDiaryColour', hex)}
                    onReset={() => updateField('defaultDiaryColour', '#c8eeee')}
                    resetLabel="Reset to Default"
                />
            </div>
        </>
    );

    const renderTeamTab = () => (
        <>
            <div className="sep-section">
                <p className="sep-section-label">Staff</p>
                <div className="sep-staff-summary">{enabledCount} of {totalCount} enabled</div>
                {serviceData.staffSettings.map((staff, i) => {
                    const isEnabled = staff.isDeactivated;
                    const price = parseFloat(staff.servicePrice) || 0;
                    const duration = parseFloat(staff.serviceTime) || 0;
                    const deposit = parseFloat(staff.depositPrice) || 0;
                    const formCount = (staff.linkedForms || []).length;

                    return (
                        <div
                            key={staff.hairdresserId || i}
                            className={`sep-staff-card ${!isEnabled ? 'sep-staff-card--disabled' : ''}`}
                            onClick={() => setSelectedStaffIndex(i)}
                        >
                            <div className="sep-staff-top">
                                <span className="sep-staff-dot" style={{ background: staff.diaryColour || serviceData.defaultDiaryColour }} />
                                <p className="sep-staff-name">{staff.name || 'Staff'}</p>
                                <svg className="sep-staff-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </div>
                            <div className="sep-staff-bottom">
                                <div className="sep-staff-meta">
                                    {deposit > 0 && <><span>{currencySymbol}{deposit.toFixed(2)} deposit</span><span className="sep-staff-meta-dot" /></>}
                                    {formCount > 0 && <><span>{formCount} {formCount === 1 ? 'form' : 'forms'}</span><span className="sep-staff-meta-dot" /></>}
                                    {isOwner && seStaffInfo(staff.hairdresserId) && staff.salonCommissionRate !== '' && staff.salonCommissionRate != null && (
                                        <><span>{parseFloat(staff.salonCommissionRate)}% commission</span><span className="sep-staff-meta-dot" /></>
                                    )}
                                    {staff.isFeaturedForStaff && <span>★ Featured</span>}
                                </div>
                                <div className="sep-staff-right">
                                    <div className="sep-staff-pricing">
                                        {duration > 0 && <span className="sep-staff-duration">{duration}m</span>}
                                        <span className="sep-staff-price">{price > 0 ? `${currencySymbol}${price.toFixed(2)}` : '-'}</span>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <Toggle
                                            checked={isEnabled}
                                            onChange={(val) => updateStaffField(i, 'isDeactivated', val)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );

    const renderStaffDetail = () => {
        if (selectedStaffIndex === null || !serviceData.staffSettings[selectedStaffIndex]) return null;
        const staff = serviceData.staffSettings[selectedStaffIndex];
        const i = selectedStaffIndex;

        return (
            <div className="sep-staff-detail" key={`staff-${i}`}>
                <div className="sep-staff-detail-header">
                    <span className="sep-staff-dot" style={{ background: staff.diaryColour || serviceData.defaultDiaryColour, width: '12px', height: '12px' }} />
                    <div>
                        <h3 className="sep-staff-detail-name">{staff.name}</h3>
                        <span className="sep-staff-detail-sub">Service settings for this team member</span>
                    </div>
                </div>
                    {/* Availability */}
                    <div className="sep-section">
                        <p className="sep-section-label">Availability</p>
                        <div className="sep-toggle-row">
                            <div className="sep-toggle-info">
                                <div className="sep-toggle-title">Service Enabled</div>
                                <div className="sep-toggle-desc">Allow this staff member to perform this service</div>
                            </div>
                            <Toggle checked={staff.isDeactivated} onChange={(val) => updateStaffField(i, 'isDeactivated', val)} />
                        </div>
                        <div className="sep-toggle-row">
                            <div className="sep-toggle-info">
                                <div className="sep-toggle-title">Online Bookings</div>
                                <div className="sep-toggle-desc">Clients can book this service online</div>
                            </div>
                            <Toggle checked={staff.onlineBooking} onChange={(val) => updateStaffField(i, 'onlineBooking', val)} />
                        </div>
                        <div className="sep-toggle-row" style={{ borderBottom: 'none' }}>
                            <div className="sep-toggle-info">
                                <div className="sep-toggle-title">Featured for Staff Page</div>
                                <div className="sep-toggle-desc">{staff.isFeaturedForStaff ? 'Shown in featured section' : 'Not featured'}</div>
                            </div>
                            <button type="button" className={`sep-star-btn ${staff.isFeaturedForStaff ? 'sep-star-btn--active' : ''}`}
                                onClick={() => updateStaffField(i, 'isFeaturedForStaff', !staff.isFeaturedForStaff)}>
                                <span>★</span>
                            </button>
                        </div>
                    </div>

                    {/* Pricing */}
                    <div className="sep-section">
                        <p className="sep-section-label">Pricing</p>
                        <div className="sep-field-row">
                            <div className="sep-field">
                                <label className="sep-field-label">Price ({currencySymbol})</label>
                                <input className="sep-input" type="number" value={staff.servicePrice}
                                    onChange={(e) => updateStaffField(i, 'servicePrice', e.target.value)} placeholder="0.00" />
                            </div>
                            <div className="sep-field">
                                <label className="sep-field-label">Deposit ({currencySymbol})</label>
                                <input className={`sep-input ${errors[`depositPrice_${i}`] ? 'sep-input--error' : ''}`}
                                    type="number" value={staff.depositPrice}
                                    onChange={(e) => updateStaffField(i, 'depositPrice', e.target.value)} placeholder="0.00" />
                                {errors[`depositPrice_${i}`] && <span className="sep-error">{errors[`depositPrice_${i}`]}</span>}
                            </div>
                        </div>
                        {isOwner && seStaffInfo(staff.hairdresserId) && (() => {
                            const staffRate = parseFloat(seStaffInfo(staff.hairdresserId).salonCommissionRate) || 0;
                            const hasOverride = staff.salonCommissionRate !== '' && staff.salonCommissionRate != null;
                            const rate = hasOverride ? parseFloat(staff.salonCommissionRate) : staffRate;
                            const price = parseFloat(staff.servicePrice) || 0;
                            const valid = !Number.isNaN(rate) && rate >= 0 && rate <= 100;
                            const toSalon = valid ? price * rate / 100 : 0;
                            const firstName = (staff.name || '').split(' ')[0] || 'stylist';
                            return (
                                <div className="sep-field-row" style={{ marginTop: '12px' }}>
                                    <div className="sep-field">
                                        <label className="sep-field-label">Salon commission (%)</label>
                                        <input className={`sep-input ${errors[`salonCommissionRate_${i}`] ? 'sep-input--error' : ''}`}
                                            type="number" min="0" max="100" step="0.5"
                                            value={staff.salonCommissionRate ?? ''}
                                            onChange={(e) => updateStaffField(i, 'salonCommissionRate', e.target.value)}
                                            placeholder={`${staffRate}% (staff rate)`} />
                                        {errors[`salonCommissionRate_${i}`]
                                            ? <span className="sep-error">{errors[`salonCommissionRate_${i}`]}</span>
                                            : <span className="sep-toggle-desc sep-commission-hint">
                                                {hasOverride
                                                    ? <>Overrides {firstName}'s {staffRate}% for this service only · <button type="button" className="sep-link-btn" onClick={() => updateStaffField(i, 'salonCommissionRate', '')}>use staff rate</button></>
                                                    : <>Self-employed · uses {firstName}'s staff rate. Set a % to override it for this service.</>}
                                                {valid && price > 0 && <><br />{currencySymbol}{toSalon.toFixed(2)} to salon · {currencySymbol}{(price - toSalon).toFixed(2)} to {firstName}, before fees</>}
                                              </span>}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Tax (per service, shared across staff) */}
                    {i === 0 && serviceId ? (
                        <div className="sep-section">
                            <TaxPicker domain="services" itemId={serviceId} />
                        </div>
                    ) : null}

                    {/* Timing */}
                    <div className="sep-section">
                        <p className="sep-section-label">Timing</p>
                        <div className="sep-field-row">
                            <div className="sep-field">
                                <label className="sep-field-label">Duration (min)</label>
                                <input className={`sep-input ${errors[`serviceTime_${i}`] ? 'sep-input--error' : ''}`}
                                    type="number" value={staff.serviceTime}
                                    onChange={(e) => updateStaffField(i, 'serviceTime', e.target.value)} placeholder="0" />
                                {errors[`serviceTime_${i}`] && <span className="sep-error">{errors[`serviceTime_${i}`]}</span>}
                            </div>
                            <div className="sep-field">
                                <label className="sep-field-label">Processing (min)</label>
                                <input className={`sep-input ${errors[`processingTime_${i}`] ? 'sep-input--error' : ''}`}
                                    type="number" value={staff.processingTime}
                                    onChange={(e) => updateStaffField(i, 'processingTime', e.target.value)} placeholder="0" />
                                <span className="sep-field-hint">Time where the client is waiting (e.g. colour developing) and you're free to take other bookings. Needs finishing time or another service after it.</span>
                                {errors[`processingTime_${i}`] && <span className="sep-error">{errors[`processingTime_${i}`]}</span>}
                            </div>
                            <div className="sep-field">
                                <label className="sep-field-label">Finishing (min)</label>
                                <input className={`sep-input ${errors[`finishingTime_${i}`] ? 'sep-input--error' : ''}`}
                                    type="number" value={staff.finishingTime || ''}
                                    onChange={(e) => updateStaffField(i, 'finishingTime', e.target.value)} placeholder="0" />
                                <span className="sep-field-hint">Back with the client after processing (e.g. rinse, tone, blow-dry)</span>
                                {errors[`finishingTime_${i}`] && <span className="sep-error">{errors[`finishingTime_${i}`]}</span>}
                            </div>
                        </div>
                        <div className="sep-field-row">
                            <div className="sep-field">
                                <label className="sep-field-label">Setup time (min)</label>
                                <input className="sep-input" type="number" value={staff.setupTime || ''}
                                    onChange={(e) => updateStaffField(i, 'setupTime', e.target.value)} placeholder="0" />
                                <span className="sep-field-hint">Buffer before appointment</span>
                            </div>
                            <div className="sep-field">
                                <label className="sep-field-label">Cleanup time (min)</label>
                                <input className="sep-input" type="number" value={staff.cleanupTime || ''}
                                    onChange={(e) => updateStaffField(i, 'cleanupTime', e.target.value)} placeholder="0" />
                                <span className="sep-field-hint">Buffer after appointment</span>
                            </div>
                        </div>
                    </div>

                    {/* Booking Rules */}
                    <div className="sep-section">
                        <p className="sep-section-label">Booking Rules</p>

                        {/* Requires finishing service */}
                        <div className="sep-toggle-row">
                            <div className="sep-toggle-info">
                                <div className="sep-toggle-title">Requires finishing service</div>
                                <div className="sep-toggle-desc">Client must also book a finishing service</div>
                            </div>
                            <Toggle checked={staff.requiresFinishing || false} onChange={(val) => {
                                updateStaffField(i, 'requiresFinishing', val);
                                if (val && !staff.finishingMessage) {
                                    updateStaffField(i, 'finishingMessage', DEFAULT_FINISHING_MESSAGE);
                                }
                                if (!val) { updateStaffField(i, 'finishingServiceIds', []); setFinishingSearch(''); }
                            }} />
                        </div>
                        <div className={`sep-expand ${staff.requiresFinishing ? 'sep-expand--open' : ''}`}>
                            {/* "Same stylist only" toggle removed for now: bookings and
                                checkout currently only support the same-stylist-only
                                behaviour, so the toggle was misleading. The
                                `finishingSameStylist` value still defaults to true via
                                `!== false`, so existing data flows untouched. Re-add the
                                toggle here when multi-stylist finishing is supported. */}
                            {/* Selected chips */}
                            {(staff.finishingServiceIds || []).length > 0 && (
                                <div className="sep-chips">
                                    {(staff.finishingServiceIds || []).map(svcId => {
                                        const svc = allServices.find(s => s.id === svcId);
                                        return svc ? (
                                            <span key={svcId} className="sep-chip">
                                                {svc.name}
                                                <button type="button" className="sep-chip-remove"
                                                    onClick={() => updateStaffField(i, 'finishingServiceIds', (staff.finishingServiceIds || []).filter(id => id !== svcId))}>
                                                    ×
                                                </button>
                                            </span>
                                        ) : null;
                                    })}
                                </div>
                            )}
                            {/* Service picker */}
                            <div className={`sep-service-picker ${errors[`finishingServiceIds_${i}`] ? 'sep-service-picker--error' : ''}`}>
                                <input type="text" className="sep-service-search" placeholder="Search finishing services..."
                                    value={finishingSearch} onChange={(e) => setFinishingSearch(e.target.value)} />
                                <div className="sep-service-list">
                                    {allServices
                                        .filter(s => !finishingSearch || s.name.toLowerCase().includes(finishingSearch.toLowerCase()))
                                        .map(svc => {
                                            const isSelected = (staff.finishingServiceIds || []).includes(svc.id);
                                            // Finishing is same-stylist (the toggle is hidden and
                                            // finishingSameStylist defaults true) — so the target
                                            // must be online-bookable by THIS stylist. Disable
                                            // non-bookable services so an unsatisfiable gate can't
                                            // be configured; still allow deselecting a stale pick.
                                            const bookable = isServiceBookableOnline(svc.id, staff.finishingSameStylist !== false, staff.hairdresserId);
                                            const disabled = !bookable && !isSelected;
                                            return (
                                                <button key={svc.id} type="button"
                                                    className={`sep-service-item ${disabled ? 'sep-service-item--disabled' : ''}`}
                                                    disabled={disabled}
                                                    title={disabled ? "This stylist doesn't offer this service for online booking, so clients can't add it as a finishing service." : undefined}
                                                    onClick={() => {
                                                        if (disabled) return;
                                                        const current = staff.finishingServiceIds || [];
                                                        updateStaffField(i, 'finishingServiceIds',
                                                            isSelected ? current.filter(id => id !== svc.id) : [...current, svc.id]);
                                                    }}>
                                                    <span className={`sep-service-check ${isSelected ? 'sep-service-check--active' : ''}`}>
                                                        {isSelected && (
                                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                    {svc.name}
                                                    {disabled && <span className="sep-service-item-tag">Not online</span>}
                                                </button>
                                            );
                                        })}
                                    {allServices.filter(s => !finishingSearch || s.name.toLowerCase().includes(finishingSearch.toLowerCase())).length === 0 && (
                                        <div className="sep-service-empty">No services found</div>
                                    )}
                                </div>
                            </div>
                            {errors[`finishingServiceIds_${i}`] && (
                                <span className="sep-error">{errors[`finishingServiceIds_${i}`]}</span>
                            )}
                            {/* Client-facing message */}
                            <div className="sep-field" style={{ marginTop: '12px' }}>
                                <label className="sep-field-label">Message for clients</label>
                                <textarea className="sep-input sep-textarea" rows={2}
                                    value={staff.finishingMessage || ''}
                                    onChange={(e) => updateStaffField(i, 'finishingMessage', e.target.value)} />
                                <span className="sep-field-hint">Shown on the booking page when this rule applies</span>
                            </div>
                        </div>

                        {/* First visit consultation */}
                        <div className="sep-toggle-row">
                            <div className="sep-toggle-info">
                                <div className="sep-toggle-title">First visit consultation</div>
                                <div className="sep-toggle-desc">New clients must complete a consultation</div>
                            </div>
                            <Toggle checked={staff.requiresConsultation || false} onChange={(val) => {
                                updateStaffField(i, 'requiresConsultation', val);
                                if (val && !staff.consultationMessage) {
                                    updateStaffField(i, 'consultationMessage', DEFAULT_CONSULTATION_MESSAGE);
                                }
                                if (!val) { updateStaffField(i, 'consultationServiceId', null); setConsultationSearch(''); }
                            }} />
                        </div>
                        <div className={`sep-expand ${staff.requiresConsultation ? 'sep-expand--open' : ''}`}>
                            {/* Same stylist toggle - FIRST */}
                            <div className="sep-toggle-row">
                                <div className="sep-toggle-info">
                                    <div className="sep-toggle-title">Same stylist only</div>
                                    <div className="sep-toggle-desc">Consultation must be with the same person</div>
                                </div>
                                <Toggle checked={staff.consultationSameStylist !== false} onChange={(val) => updateStaffField(i, 'consultationSameStylist', val)} />
                            </div>
                            {/* Consultation service picker (same UI as finishing, but single-select) */}
                            <div className={`sep-service-picker ${errors[`consultationServiceId_${i}`] ? 'sep-service-picker--error' : ''}`}>
                                <input type="text" className="sep-service-search" placeholder="Search consultation services..."
                                    value={consultationSearch} onChange={(e) => setConsultationSearch(e.target.value)} />
                                <div className="sep-service-list">
                                    {allServices
                                        .filter(s => !consultationSearch || s.name.toLowerCase().includes(consultationSearch.toLowerCase()))
                                        .map(svc => {
                                            const isSelected = staff.consultationServiceId === svc.id;
                                            // Honour the "Same stylist only" toggle: if on, the
                                            // consultation must be online-bookable by THIS stylist;
                                            // if off, by any stylist. Disable non-bookable picks so
                                            // an unsatisfiable gate can't be configured; still allow
                                            // deselecting the current one.
                                            const bookable = isServiceBookableOnline(svc.id, staff.consultationSameStylist !== false, staff.hairdresserId);
                                            const disabled = !bookable && !isSelected;
                                            return (
                                                <button key={svc.id} type="button"
                                                    className={`sep-service-item ${disabled ? 'sep-service-item--disabled' : ''}`}
                                                    disabled={disabled}
                                                    title={disabled ? ((staff.consultationSameStylist !== false)
                                                        ? "This stylist doesn't offer this service for online booking, so clients can't add it as a consultation."
                                                        : "No stylist offers this service for online booking, so clients can't add it as a consultation.") : undefined}
                                                    onClick={() => { if (!disabled) updateStaffField(i, 'consultationServiceId', isSelected ? null : svc.id); }}>
                                                    <span className={`sep-service-check ${isSelected ? 'sep-service-check--active' : ''}`}>
                                                        {isSelected && (
                                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                    {svc.name}
                                                    {disabled && <span className="sep-service-item-tag">Not online</span>}
                                                </button>
                                            );
                                        })}
                                    {allServices.filter(s => !consultationSearch || s.name.toLowerCase().includes(consultationSearch.toLowerCase())).length === 0 && (
                                        <div className="sep-service-empty">No services found</div>
                                    )}
                                </div>
                            </div>
                            {errors[`consultationServiceId_${i}`] && (
                                <span className="sep-error">{errors[`consultationServiceId_${i}`]}</span>
                            )}
                            {/* Client-facing message */}
                            <div className="sep-field" style={{ marginTop: '12px' }}>
                                <label className="sep-field-label">Message for clients</label>
                                <textarea className="sep-input sep-textarea" rows={2}
                                    value={staff.consultationMessage || ''}
                                    onChange={(e) => updateStaffField(i, 'consultationMessage', e.target.value)} />
                                <span className="sep-field-hint">Shown on the booking page for new clients</span>
                            </div>
                        </div>
                    </div>

                    {/* Appearance */}
                    <div className="sep-section">
                        <p className="sep-section-label">Appearance</p>
                        <ColourPicker
                            value={staff.diaryColour || serviceData.defaultDiaryColour}
                            onChange={(hex) => { updateStaffField(i, 'diaryColour', hex); updateStaffField(i, 'isColourOverridden', true); }}
                            onReset={() => { updateStaffField(i, 'diaryColour', serviceData.defaultDiaryColour); updateStaffField(i, 'isColourOverridden', false); }}
                            resetLabel="Reset to Service Default"
                        />
                    </div>

                    {/* Portfolio Images */}
                    <div className="sep-section">
                        <p className="sep-section-label">Portfolio Images</p>
                        <div className="sep-images-grid">
                            {(staff.staffServiceImages || []).map((imageUrl, imgIndex) => (
                                <div key={imgIndex} className="sep-image-item">
                                    <img src={imageUrl} alt="" />
                                    <button type="button" className="sep-image-delete"
                                        onClick={() => {
                                            updateStaffField(i, 'staffServiceImages', staff.staffServiceImages.filter((_, idx) => idx !== imgIndex));
                                        }}>×</button>
                                </div>
                            ))}
                            <div className="sep-image-upload" style={{ position: 'relative' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                                <span>Add</span>
                                <input type="file" accept="image/*" onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('image', file);
                                    dispatch(newServicesAction.uploadServiceImage(formData))
                                        .then(res => {
                                            const url = res?.uploadImage?.responseData?.url || res?.url;
                                            if (url) updateStaffField(i, 'staffServiceImages', [...(staff.staffServiceImages || []), url]);
                                        }).catch(() => {});
                                    e.target.value = '';
                                }} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                            </div>
                        </div>
                    </div>

            </div>
        );
    };

    const editorContent = loading ? (
        <>
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">Loading...</h2>
                </div>
                <button className="beu-icon-btn" onClick={() => handleClose()}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>
            <div className="sep-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="sdp-shimmer" style={{ width: '60%' }} />
            </div>
        </>
    ) : (
        <>
            {/* Header - matches sdp-header pattern */}
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">{serviceData.serviceName || (serviceId ? 'Edit Service' : 'New Service')}</h2>
                    {activeTab === 'team' && selectedStaffIndex !== null && serviceData.staffSettings[selectedStaffIndex] && (
                        <span className="sdp-header-category">{serviceData.staffSettings[selectedStaffIndex].name}</span>
                    )}
                </div>
                <button className="beu-icon-btn" onClick={() => handleClose()}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            {/* Steps */}
            <div className="sep-steps">
                {STEPS.map(step => (
                    <button
                        key={step.id}
                        className={`sep-step ${activeTab === step.id ? 'sep-step--active' : ''}`}
                        onClick={() => { setActiveTab(step.id); setSelectedStaffIndex(null); }}
                    >
                        <span className="sep-step-number">{step.number}</span>
                        <span className="sep-step-label">{step.label}</span>
                    </button>
                ))}
            </div>

            {/* Body */}
            <div className="sep-body" key={activeTab === 'team' && selectedStaffIndex !== null ? `staff-${selectedStaffIndex}` : activeTab}>
                {activeTab === 'detail' && renderDetailTab()}
                {activeTab === 'team' && selectedStaffIndex === null && renderTeamTab()}
                {activeTab === 'team' && selectedStaffIndex !== null && renderStaffDetail()}
            </div>

            {/* Footer — Cancel + Save normally. Inside a single stylist's
                detail, just a full-width Done — staff field changes are kept
                in local state until the user taps the top-level Save (so
                they can edit several stylists in a row without committing
                each one separately). */}
            <div className="sdp-footer">
                {activeTab === 'team' && selectedStaffIndex !== null ? (
                    <button
                        type="button"
                        className="sdp-btn sdp-btn--primary"
                        onClick={() => setSelectedStaffIndex(null)}
                    >
                        Done
                    </button>
                ) : (
                    <>
                        <button type="button" className="sdp-btn sdp-btn--neutral" onClick={() => handleClose()}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="sdp-btn sdp-btn--primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </>
                )}
            </div>
        </>
    );

    if (inline) return editorContent;

    return (
        <SlidePanel ref={slidePanelRef} onClose={onClose} className="sep-panel" width={700}>
            {editorContent}
        </SlidePanel>
    );
};

export default ServiceEditorPanel;
